import { useEffect, useState } from "react";
import {
  LINKEDIN,
  ADS_CAMPAIGN_LINKEDIN_CAROUSEL_MIN_CARDS,
} from "../../../../utils/constants";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useFormContext } from "react-hook-form";
import useFocusErrorForm from "../../../../hooks/useFocusErrorForm";
import { useTranslationApp } from "../../../../lib/i18next";
import { waitDelay } from "../../../../utils/date";
import { getUniqueID } from "../../../../utils/numbers";
import { useMediaQuery } from "@mui/material";
import {
  SELECT_AD_FORMAT_TO_CONTINUE,
  ADS_CAMPAIGN_PREVIEW,
} from "../../../../i18n/keysTranslations";
import AssetsGroupLinkedinImageForm from "./AssetsGroupLinkedinImageForm";
import AssetsGroupLinkedinVideoForm from "./AssetsGroupLinkedinVideoForm";
import AssetsGroupLinkedinCarouselForm from "./AssetsGroupLinkedinCarouselForm";
import AdPreviewLinkedin from "../Ad/AdPreviewLinkedin";
import CardContainer from "../../../Card/CardContainer";

const platform = LINKEDIN;

// Ad format constants
const AD_FORMAT_IMAGE = "STANDARD_UPDATE";
const AD_FORMAT_VIDEO = "SINGLE_VIDEO";
const AD_FORMAT_CAROUSEL = "CAROUSEL";

const AssetsGroupLinkedinForm = () => {
  const [isLoading, setIsLoading] = useState(true);
  const {
    control,
    formState,
    watch,
    sectionSelected,
    setValue,
    getValues,
    isGeneralDisabledFields,
    trigger,
  } = useFormContext();
  useFocusErrorForm(formState);

  const errors = formState.errors;
  const { t } = useTranslationApp();

  const breakColumnFormAndPreview = useMediaQuery(() => "(max-width: 1080px)");

  const platformsData = watch("platformsData") || {};
  const platformData = platformsData?.[platform] || {};
  const adFormat = platformData?.adFormat;
  const objective = platformData?.objective;
  const isIncompatibleCampaign = platformData?.isIncompatible;

  const assetsGroups = watch("assetsGroups");
  const assetGroupID = sectionSelected.assetsGroupID;
  const index = assetsGroups.findIndex((item) => item.id === assetGroupID);
  const BASIC_PATH = `assetsGroups.[${index}]`;
  const isIncompatibleAssetsGroup =
    getValues(`${BASIC_PATH}.isIncompatible`) || false;

  const isDisabledFields =
    isGeneralDisabledFields ||
    isIncompatibleCampaign ||
    isIncompatibleAssetsGroup;

  // Normalize assetsGroup shape based on adFormat
  useEffect(() => {
    if (!adFormat || index < 0) return;

    const currentTitles = getValues(`${BASIC_PATH}.titles`) || [];
    const currentHeadlines = getValues(`${BASIC_PATH}.headlines`) || [];
    const currentDescriptions = getValues(`${BASIC_PATH}.descriptions`) || [];
    const currentMediaUrls = getValues(`${BASIC_PATH}.mediaUrls`) || [];
    const currentDestinationUrls =
      getValues(`${BASIC_PATH}.destinationUrls`) || [];

    // Ensure titles always has at least 1 item
    if (currentTitles.length === 0) {
      setValue(`${BASIC_PATH}.titles`, [{ id: getUniqueID(), value: "" }]);
    }

    // Ensure descriptions has at least 1 item for Image format
    if (adFormat === AD_FORMAT_IMAGE && currentDescriptions.length === 0) {
      setValue(`${BASIC_PATH}.descriptions`, [
        { id: getUniqueID(), value: "" },
      ]);
    }

    // Handle headlines based on format
    if (adFormat === AD_FORMAT_IMAGE || adFormat === AD_FORMAT_VIDEO) {
      // Single format: ensure at least 1 headline
      if (currentHeadlines.length === 0) {
        setValue(`${BASIC_PATH}.headlines`, [{ id: getUniqueID(), value: "" }]);
      }
      // Single format: ensure at least 1 mediaUrl
      if (currentMediaUrls.length === 0) {
        setValue(`${BASIC_PATH}.mediaUrls`, [""]);
      }
    } else if (adFormat === AD_FORMAT_CAROUSEL) {
      // Carousel: ensure minimum cards
      const minCards = ADS_CAMPAIGN_LINKEDIN_CAROUSEL_MIN_CARDS;

      // Ensure mediaUrls has at least minCards items
      if (currentMediaUrls.length < minCards) {
        const newMediaUrls = [...currentMediaUrls];
        while (newMediaUrls.length < minCards) {
          newMediaUrls.push("");
        }
        setValue(`${BASIC_PATH}.mediaUrls`, newMediaUrls);
      }

      // Ensure headlines has at least as many items as mediaUrls
      const targetLength = Math.max(currentMediaUrls.length, minCards);
      if (currentHeadlines.length < targetLength) {
        const newHeadlines = [...currentHeadlines];
        while (newHeadlines.length < targetLength) {
          newHeadlines.push({ id: getUniqueID(), value: "" });
        }
        setValue(`${BASIC_PATH}.headlines`, newHeadlines);
      }

      // Ensure destinationUrls has at least as many items as mediaUrls
      if (currentDestinationUrls.length < targetLength) {
        const newDestinationUrls = [...currentDestinationUrls];
        while (newDestinationUrls.length < targetLength) {
          newDestinationUrls.push({ id: getUniqueID(), value: "" });
        }
        setValue(`${BASIC_PATH}.destinationUrls`, newDestinationUrls);
      }
    }
    // eslint-disable-next-line
  }, [adFormat, sectionSelected]);

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      await waitDelay(1);
      setIsLoading(false);
    })();
  }, [sectionSelected]);

  if (isLoading) return null;

  // If no adFormat selected, show message
  if (!adFormat) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "300px",
          gap: 2,
        }}
      >
        <Typography variant="h6" color="text.secondary">
          {t(SELECT_AD_FORMAT_TO_CONTINUE)}
        </Typography>
      </Box>
    );
  }

  // Get values for preview
  const adID = watch(`${BASIC_PATH}.id`);
  const mediaUrls = watch(`${BASIC_PATH}.mediaUrls`) || [];
  const mediaData = watch(`${BASIC_PATH}.mediaData`) || [];
  const titles = watch(`${BASIC_PATH}.titles`) || [];
  const headlines = watch(`${BASIC_PATH}.headlines`) || [];
  const descriptions = watch(`${BASIC_PATH}.descriptions`) || [];

  // Handle destinationUrls - can be array or single value depending on format
  let destinationUrls = watch(`${BASIC_PATH}.destinationUrls`) || [];
  const destinationUrl = watch(`${BASIC_PATH}.destinationUrl`);

  // If destinationUrl exists (Video format), convert to array format
  if (destinationUrl && !destinationUrls?.length) {
    destinationUrls = [{ id: getUniqueID(), value: destinationUrl }];
  }

  // Ensure destinationUrls is an array
  if (!Array.isArray(destinationUrls)) {
    destinationUrls = [];
  }

  // Common props for all form components
  const commonProps = {
    BASIC_PATH,
    control,
    errors,
    trigger,
    setValue,
    getValues,
    watch,
    isDisabledFields,
    platform,
    objective,
    breakColumnFormAndPreview,
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: breakColumnFormAndPreview ? "column" : "row",
        gap: 3,
        width: "100%",
      }}
    >
      {/* Form Column */}
      <Box sx={{ flex: 1 }}>
        {adFormat === AD_FORMAT_IMAGE && (
          <AssetsGroupLinkedinImageForm {...commonProps} />
        )}
        {adFormat === AD_FORMAT_VIDEO && (
          <AssetsGroupLinkedinVideoForm {...commonProps} />
        )}
        {adFormat === AD_FORMAT_CAROUSEL && (
          <AssetsGroupLinkedinCarouselForm {...commonProps} />
        )}
      </Box>

      {/* Preview Column */}
      {adFormat !== AD_FORMAT_CAROUSEL && (
        <Box
          sx={{
            flex: 1,
            width: "100%",
            maxWidth: 400,
            height: "100%",
          }}
        >
          <CardContainer
            dynamicHeight={true}
            title={
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t(ADS_CAMPAIGN_PREVIEW)}
                </Typography>
              </Box>
            }
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <AdPreviewLinkedin
                adID={adID}
                mediaUrls={mediaUrls}
                mediaData={mediaData}
                titles={titles}
                headlines={headlines}
                descriptions={descriptions}
                destinationUrls={destinationUrls}
                basicPath={BASIC_PATH}
                basicPathAdGroup={BASIC_PATH}
              />
            </Box>
          </CardContainer>
        </Box>
      )}
    </Box>
  );
};

export default AssetsGroupLinkedinForm;
